import { FormEvent, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { authBackgroundPatternUrl, primaryLogoPngUrl } from "@/config/public-assets";
import {
  forgotAdminPassword,
  startAdminLogin,
} from "@/features/auth/api/adminAuthApi";
import type {
  ForgotPasswordLocationState,
  VerifyOtpLocationState,
} from "@/features/auth/types/loginFlow";
import { authMessages } from "@/features/auth/messages";
import { isValidEmail } from "@/features/auth/utils/isValidEmail";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import styles from "./LoginPage.module.css";

type LoginLocationState = {
  sessionExpired?: boolean;
  passwordResetSuccess?: boolean;
};

export default function LoginPage() {
  useDocumentTitle(authMessages.documentTitle.login);
  const { brand, login: loginMsg } = authMessages;

  const navigate = useNavigate();
  const location = useLocation();
  const loginState = location.state as LoginLocationState | null;
  const sessionExpired = loginState?.sessionExpired === true;
  const passwordResetSuccess = loginState?.passwordResetSuccess === true;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isForgotPasswordSubmitting, setIsForgotPasswordSubmitting] =
    useState(false);
  const [error, setError] = useState<string | null>(null);

  const isBusy = isSubmitting || isForgotPasswordSubmitting;
  const canSignIn =
    isValidEmail(email) && password.length > 0 && !isBusy;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSignIn) {
      return;
    }
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const emailTrimmed = email.trim();
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await startAdminLogin({
        email: emailTrimmed,
        password,
      });

      const nextState: VerifyOtpLocationState = {
        email: emailTrimmed,
        otpSentTo: result.otp_sent_to,
        otpExpiresInSeconds: result.expires_in_seconds,
        maxAttempts: result.max_attempts,
        maxResends: result.max_resends,
      };

      navigate("/login/verify-otp", {
        replace: true,
        state: nextState,
      });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : loginMsg.signInFailed;
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleForgotPassword() {
    const emailTrimmed = email.trim();
    if (!isValidEmail(emailTrimmed)) {
      setError(loginMsg.forgotPasswordEmailRequired);
      return;
    }

    setError(null);
    setIsForgotPasswordSubmitting(true);

    try {
      const result = await forgotAdminPassword({ email: emailTrimmed });
      const nextState: ForgotPasswordLocationState = {
        email: emailTrimmed,
        message: result.message,
        hint: result.hint,
      };

      navigate("/login/reset-password", {
        replace: true,
        state: nextState,
      });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : loginMsg.forgotPasswordFailed,
      );
    } finally {
      setIsForgotPasswordSubmitting(false);
    }
  }

  return (
    <div
      className={styles.page}
      style={{ backgroundImage: `url(${authBackgroundPatternUrl})` }}
      data-testid="login-page"
    >
      <main
        className={styles.card}
        aria-labelledby="login-heading"
        data-testid="login-card"
      >
        <div className={styles.brandH} data-testid="login-brand">
          <img
            src={primaryLogoPngUrl}
            alt={brand.name}
            className={styles.brandLogoFull}
            decoding="async"
            referrerPolicy="no-referrer"
          />
          <span className={styles.brandAdmin}>{brand.adminPortal}</span>
        </div>

        <div
          className={styles.twoStepPill}
          role="note"
          aria-label={loginMsg.twoStepPillAriaLabel}
          data-testid="login-two-step-pill"
        >
          <span className={styles.twoStepIcon} aria-hidden>
            🔒
          </span>
          {loginMsg.twoStepPill}
        </div>

        {sessionExpired ? (
          <p
            className={styles.sessionExpiredNotice}
            role="alert"
            data-testid="login-session-expired-notice"
          >
            {loginMsg.sessionExpired}
          </p>
        ) : null}

        {error ? (
          <p
            className={styles.formError}
            role="alert"
            data-testid="login-error-message"
          >
            {error}
          </p>
        ) : null}

        {passwordResetSuccess ? (
          <p
            className={styles.formSuccess}
            role="status"
            data-testid="login-password-reset-success"
          >
            {loginMsg.passwordResetSuccess}
          </p>
        ) : null}

        <h1
          id="login-heading"
          className={styles.heading}
          data-testid="login-title"
        >
          {loginMsg.title}
        </h1>
        <p className={styles.subtext} data-testid="login-subtitle">
          {loginMsg.subtitle}
        </p>

        <form
          className={styles.form}
          onSubmit={handleSubmit}
          data-testid="login-form"
        >
          <div className={styles.field} data-testid="login-email-field">
            <label className={styles.label} htmlFor="login-email">
              {loginMsg.workEmail}
            </label>
            <input
              id="login-email"
              name="email"
              className={styles.input}
              type="email"
              autoComplete="username"
              inputMode="email"
              placeholder={loginMsg.emailPlaceholder}
              value={email}
              onChange={(ev) => {
                setEmail(ev.target.value);
                setError(null);
              }}
              disabled={isBusy}
              required
              aria-required="true"
              data-testid="login-email-input"
            />
          </div>

          <div className={styles.field} data-testid="login-password-field">
            <label className={styles.label} htmlFor="login-password">
              {loginMsg.password}
            </label>
            <div className={styles.passwordInputWrap}>
              <input
                id="login-password"
                name="password"
                className={styles.passwordInput}
                type={isPasswordVisible ? "text" : "password"}
                autoComplete="current-password"
                placeholder={loginMsg.passwordPlaceholder}
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                disabled={isBusy}
                required
                aria-required="true"
                data-testid="login-password-input"
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setIsPasswordVisible((visible) => !visible)}
                aria-label={
                  isPasswordVisible
                    ? loginMsg.hidePassword
                    : loginMsg.showPassword
                }
                aria-pressed={isPasswordVisible}
                disabled={isBusy}
                data-testid="login-password-visibility-toggle"
              >
                {isPasswordVisible ? (
                  <svg
                    className={styles.passwordToggleIcon}
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M3 3l18 18M10.58 10.58A2 2 0 0 0 12 15a2 2 0 0 0 1.42-.58M9.88 5.09A10.94 10.94 0 0 1 12 5c5 0 9.27 3.11 11 7.5a11.8 11.8 0 0 1-1.67 2.83M6.11 6.11A11.75 11.75 0 0 0 1 12.5C2.73 16.89 7 20 12 20a10.9 10.9 0 0 0 5.11-1.27"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    className={styles.passwordToggleIcon}
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M2 12C3.73 7.11 7.5 4 12 4s8.27 3.11 10 8c-1.73 4.89-5.5 8-10 8S3.73 16.89 2 12Z"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      stroke="currentColor"
                      strokeWidth="1.75"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className={styles.submit}
            disabled={!canSignIn}
            aria-busy={isSubmitting}
            data-testid="login-submit-button"
          >
            {isSubmitting ? loginMsg.submitting : loginMsg.submit}
          </button>
        </form>

        <div className={styles.footerLinks} data-testid="login-footer-links">
          <button
            type="button"
            className={styles.footerLink}
            disabled={isBusy}
            onClick={() => void handleForgotPassword()}
            data-testid="login-forgot-password-link"
          >
            {isForgotPasswordSubmitting
              ? loginMsg.forgotPasswordSubmitting
              : loginMsg.forgotPassword}
          </button>
        </div>
      </main>
    </div>
  );
}
